import React, { useState, useEffect, useRef } from "react";
import { Input } from "reactstrap";
import axios from "axios";
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import webservice from "../../../services/webservice";
import { AesEncrypt, AesDecrypt } from "../../../services/crypto";
import { FaUsers, FaEye, FaMousePointer } from "react-icons/fa";
import Swal from 'sweetalert2';
import './analytics-master.css';
import DemographicScreen from "./DemographicScreen";
import { useNavigate } from "react-router-dom";
import { ComponentLoader } from '../common/Loader';

const ScreenAuditlog = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState({
    totalScreenViews: null,
    activeUsers: null,
    interactions: null,
    topScreens: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination & Sorting
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'count', direction: 'desc' });
  const [filters, setFilters] = useState({ searchName: '', searchViews: '' });

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchAnalytics = async () => {
      try {
        const cachedData = sessionStorage.getItem('SCREEN_AUDIT_LOG_CACHE');
        let cacheExists = false;

        if (cachedData) {
          const data = JSON.parse(cachedData);
          if (data === 'PENDING') {
            return;
          }

          if (data && typeof data === 'object') {
            const cacheTimestamp = data.cacheTimestamp || 0;
            const threeMinutes = 3 * 60 * 1000;
            const isExpired = Date.now() - cacheTimestamp > threeMinutes;

            if (!isExpired) {
              setAnalytics(data.analytics);
              setLoading(false);
              return;
            } else {
              setAnalytics(data.analytics);
              setLoading(false);
              cacheExists = true;
            }
          }
        }

        if (!cacheExists) {
          setLoading(true);
        }

        sessionStorage.setItem('SCREEN_AUDIT_LOG_CACHE', JSON.stringify('PENDING'));

        const encryptedPayload = AesEncrypt({});
        const response = await axios.post(`${webservice}api/screen-auditlog`, {
          EncData: encryptedPayload,
        });

        if (response.data.response === 'S') {
          const decrypted = AesDecrypt(response.data.data);
          const newAnalytics = {
            totalScreenViews: decrypted.topScreens?.length ?? "—",
            activeUsers: decrypted.activeUsers ?? "—",
            interactions: decrypted.interactions ?? "—",
            topScreens: decrypted.topScreens ?? [],
          };
          setAnalytics(newAnalytics);
          sessionStorage.setItem('SCREEN_AUDIT_LOG_CACHE', JSON.stringify({
            analytics: newAnalytics,
            cacheTimestamp: Date.now()
          }));
        } else {
          setAnalytics({
            totalScreenViews: "—",
            activeUsers: "—",
            interactions: "—",
            topScreens: [],
          });
          Swal.fire({
            icon: "warning",
            title: "Failed",
            text: response.data.message || "Could not load analytics data",
          });
          sessionStorage.removeItem('SCREEN_AUDIT_LOG_CACHE');
        }
      } catch (err) {
        console.error("Failed to load analytics:", err);
        setError("Failed to load dashboard data. Please try again later.");
        sessionStorage.removeItem('SCREEN_AUDIT_LOG_CACHE');
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Could not connect to server",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  const formatNumber = (num) => {
    if (num === "—" || num == null) return "—";
    return Number(num).toLocaleString();
  };

  const sortedScreens = React.useMemo(() => {
    let sortable = [...(analytics.topScreens || [])];

    if (filters.searchName) {
      try {
        const regex = new RegExp(filters.searchName, 'i');
        sortable = sortable.filter(item => regex.test(item.name));
      } catch (e) {
        sortable = sortable.filter(item => item.name?.toLowerCase().includes(filters.searchName.toLowerCase()));
      }
    }

    if (filters.searchViews) {
      sortable = sortable.filter(item => item.count === Number(filters.searchViews));
    }

    if (sortConfig !== null) {
      sortable.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortable;
  }, [analytics.topScreens, sortConfig, filters]);

  // Pagination calculation
  const totalItems = sortedScreens.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedScreens.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortHeader = (label, key, className) => (
    <th className={className} onClick={() => requestSort(key)} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{label}</span>
        <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', fontSize: '11px', gap: '4px' }}>
          <FaChevronUp color={sortConfig?.key === key && sortConfig.direction === 'asc' ? '#000' : '#cbd5e1'} />
          <FaChevronDown color={sortConfig?.key === key && sortConfig.direction === 'desc' ? '#000' : '#cbd5e1'} />
        </span>
      </div>
    </th>
  );
  if (error) {
    return (
      <div className="analytics-wrapper">
        <main className="analytics-main">
          <div className="analytics-header">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <div>
                <h2 style={{ fontWeight: "bold", margin: 0 }}>Screen Views Analytics</h2>
                <p style={{ margin: "0.35rem 0 0 0", color: "var(--text-muted)" }}>
                  Real-time screen views and user interactions
                </p>
              </div>
              <div className="header-actions">
                <button
                  className="analytics-btn analytics-btn--primary"
                  onClick={() => navigate("/DemographicScreen")}
                >
                  Demographic Insights
                </button>
                <button
                  className="analytics-btn analytics-btn--primary"
                  onClick={() => navigate("/screen-insights")}
                >
                  Screen wise filter
                </button>
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "3rem", color: "#dc2626" }}>
            {error}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="analytics-wrapper">
      <main className="analytics-main">
        <div className="analytics-header">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div>
              <h2 style={{ fontWeight: "bold", margin: 0 }}>Screen Views Analytics</h2>
              <p style={{ margin: "0.35rem 0 0 0", color: "var(--text-muted)" }}>
                Real-time screen views and user interactions
              </p>
            </div>

            <div className="header-actions">
              <button
                className="analytics-btn analytics-btn--primary"
                onClick={() => navigate("/DemographicScreen")}
              >
                Demographic Insights
              </button>

              <button
                className="analytics-btn analytics-btn--primary"
                onClick={() => navigate("/screen-insights")}
              >
                Screen wise filter
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-text">
                <h3>Total Unique Screens</h3>
                <p className="value">{analytics.totalScreenViews !== null ? formatNumber(analytics.totalScreenViews) : <ComponentLoader color="#4f46e5" size="14px" padding="0" />}</p>
              </div>
              <div className="stat-icon-wrapper icon-views">
                <FaEye />
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-text">
                <h3>Active Users</h3>
                <p className="value">{analytics.activeUsers !== null ? formatNumber(analytics.activeUsers) : <ComponentLoader color="#4f46e5" size="14px" padding="0" />}</p>
              </div>
              <div className="stat-icon-wrapper icon-users">
                <FaUsers />
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-text">
                <h3>Total Interactions</h3>
                <p className="value">{analytics.interactions !== null ? formatNumber(analytics.interactions) : <ComponentLoader color="#4f46e5" size="14px" padding="0" />}</p>
              </div>
              <div className="stat-icon-wrapper icon-actions">
                <FaMousePointer />
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <section className="table-section">
          <div className="table-header">
            <h4 style={{ fontWeight: "bold" }}>Screen Engagement Overview</h4>
            {totalItems > 0 && (
              <div className="showing-info">
                Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, totalItems)} of {formatNumber(totalItems)}
              </div>
            )}
          </div>
          <div className="pq-filter-container mb-4" style={{ width: '40%', minWidth: '400px', display: 'flex', flexWrap: 'wrap', gap: '15px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '15px' }}>
            <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Screen Name</label>
                <Input 
                    type="text" 
                    placeholder="Search by Screen Name..." 
                    value={filters.searchName} 
                    onChange={(e) => { setFilters(prev => ({ ...prev, searchName: e.target.value })); setCurrentPage(1); }}
                    className="pq-filter-input"
                />
            </div>
            <div style={{ flex: '0 1 120px', minWidth: '100px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Views</label>
                <Input 
                    type="number" 
                    placeholder="Exact Views..." 
                    value={filters.searchViews} 
                    onChange={(e) => { setFilters(prev => ({ ...prev, searchViews: e.target.value })); setCurrentPage(1); }}
                    className="pq-filter-input"
                />
            </div>
            {(filters.searchName || filters.searchViews) && (
                <div style={{ display: 'flex', alignItems: 'flex-end', flex: '0 0 auto' }}>
                    <button 
                        onClick={() => { setFilters({ searchName: '', searchViews: '' }); setCurrentPage(1); }}
                        style={{ height: '38px', padding: '0 16px', background: '#f87171', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '13px', marginBottom: '2px' }}
                    >
                        Clear
                    </button>
                </div>
            )}
          </div>
          <div className="table-scroll-wrapper" style={{ maxHeight: '450px', overflowY: 'auto' }}>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <ComponentLoader color="#4f46e5" size="40px" />
              </div>
            ) : totalItems > 0 ? (
              <>
                <table style={{ position: 'relative' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fdfbfb', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <tr>
                      <th className="col-rank">#</th>
                      {renderSortHeader("Screen Name", "name", "col-name")}
                      {renderSortHeader("Views", "count", "col-views")}
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((screen, index) => (
                      <tr key={startIndex + index}>
                        <td className="col-rank">{startIndex + index + 1}</td>
                        <td className="col-name">
                          <span
                            style={{ cursor: "pointer", color: "var(--accent)", fontWeight: "600" }}
                            onClick={() => navigate(`/screen-insights/${encodeURIComponent(screen.name)}`)}
                          >
                            {screen.name || "Unknown"}
                          </span>
                        </td>
                        <td className="col-views">{formatNumber(screen.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="empty-state">
                No screen activity recorded yet
              </div>
            )}
          </div>
          {/* Pagination Outside Table Wrapper */}
          {!loading && totalItems > 0 && totalPages > 0 && (
            <div className="pq-pagination-container d-flex justify-content-between align-items-center" style={{ padding: '16px', borderTop: '1px solid #e2e8f0', marginTop: '1rem' }}>
              <div className="d-flex align-items-center">
                  <span className="pq-pagination-text me-2">Showing {totalItems > 0 ? startIndex + 1 : 0} – {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems} entries. Per page: </span>
                  <Input type="select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="pq-filter-input" style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important' }}>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                  </Input>
              </div>

              <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item pq-page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link pq-page-link" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                          &laquo;
                      </button>
                  </li>

                  {[...Array(totalPages)].map((_, i) => {
                      if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                          return (
                              <li className={`page-item pq-page-item ${i + 1 === currentPage ? 'active' : ''}`} key={i}>
                                  <button className="page-link pq-page-link" onClick={() => goToPage(i + 1)}>
                                      {i + 1}
                                  </button>
                              </li>
                          );
                      }
                      if (i === 1 && currentPage > 3) return <li className="page-item pq-page-item disabled" key="start-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                      if (i === totalPages - 2 && currentPage < totalPages - 2) return <li className="page-item pq-page-item disabled" key="end-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                      return null;
                  })}

                  <li className={`page-item pq-page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                      <button className="page-link pq-page-link" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
                          &raquo;
                      </button>
                  </li>
              </ul>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ScreenAuditlog;
